
import { X, User } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

interface ProfileNotificationProps {
  userName: string;
  role: 'employer' | 'candidate' | 'administrator';
  isProfileComplete: boolean;
}

export function ProfileNotification({ userName, role, isProfileComplete }: ProfileNotificationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  
  // Check if the notification has been dismissed before
  useEffect(() => {
    const isDismissed = localStorage.getItem(`profile-notification-dismissed-${role}`);
    
    if (!isProfileComplete && !isDismissed) {
      setIsVisible(true);
    }
  }, [isProfileComplete, role]);
  
  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(`profile-notification-dismissed-${role}`, 'true');
  };
  
  const handleCompleteProfile = () => {
    navigate('/profile');
  };
  
  if (!isVisible) return null;
  
  const isEmployer = role === 'employer' || role === 'administrator';
  
  return (
    <div className="mb-8 p-4 border border-border rounded-sm bg-card shadow-sm">
      <div className="flex justify-between items-start">
        <div className="flex items-start gap-3">
          <div className="bg-primary/10 p-2 rounded-full">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-medium text-card-foreground">Complete your profile</h3>
            <p className="text-muted-foreground text-sm mt-1">
              Hey {userName}, please complete your {isEmployer ? 'company' : 'candidate'} profile 
              to get the most out of Testera.
            </p>
          </div>
        </div>
        
        <button 
          onClick={handleDismiss} 
          className="text-muted-foreground hover:text-foreground"
          aria-label="Dismiss notification"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      <div className="mt-4 flex justify-end">
        <Button 
          onClick={handleCompleteProfile}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          Complete Profile
        </Button>
      </div>
    </div>
  );
}
